import React, { useState, useEffect } from 'react';
import './TasksPage.css';
import TaskCard from '../components/TaskList/TaskCard';
import { useTasks } from '../hooks/useTasks';
import { getTaskCardClassName, getTaskStatusLabel } from '../utils/helpers';
import { fetchProfileById } from '../services/profileService';

const getYouTubeThumbnail = (url) => {
  if (!url || typeof url !== "string") return null;

  try {
    let normalizedUrl = url.trim();

    if (!normalizedUrl.startsWith("http://") && !normalizedUrl.startsWith("https://")) {
      normalizedUrl = "https://" + normalizedUrl;
    }

    const parsedUrl = new URL(normalizedUrl);
    let videoId = null;

    // youtube.com format
    if (parsedUrl.hostname.includes("youtube.com")) {
      if (parsedUrl.pathname.startsWith("/shorts/")) {
        // e.g., /shorts/VIDEO_ID
        videoId = parsedUrl.pathname.split("/shorts/")[1];
      } else {
        videoId = parsedUrl.searchParams.get("v");
      }
    } else if (parsedUrl.hostname.includes("youtu.be")) {
      // youtu.be format
      videoId = parsedUrl.pathname.split("/")[1];
    }

    if (videoId) {
      // i.ytimg.com is generally more robust than img.youtube.com
      return {
        primary: `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`,
        fallback: `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`
      };
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Student task lifecycle page.
 * @param {{ user: { id: string } | null }} props
 */
function TasksPage({ user }) {
  const [userProfile, setUserProfile] = useState(null);

  useEffect(() => {
    if (user?.id) {
      fetchProfileById(user.id).then(({ data }) => {
        if (data) setUserProfile(data);
      });
    }
  }, [user]);

  const {
    userTasks,
    loading,
    error,
    selectedTask,
    setSelectedTask,
    showProofModal,
    taskToComplete,
    successMessage,
    handleStatusChange,
    handleSubmitForApproval,
    handleRetryTask,
    closeTaskDetailsModal,
  } = useTasks(user);

  const handleSendProofEmail = () => {
    const to = "tasksquare@duck.com";

    const displayName = userProfile?.full_name || user.user_metadata?.full_name || user.username || "";
    const taskName = taskToComplete?.title || "a Task";
    const subject = `Proof of Work by ${displayName} on ${taskName}`;

    const body = `
Name: ${displayName}
Email: ${user.email || ""}
Branch: ${userProfile?.branch || user.user_metadata?.branch || user.branch || ""}
Year: ${userProfile?.academic_year || user.user_metadata?.year || user.year || ""}

Paste your proof of work here:
`;

    const gmailUrl =
      `https://mail.google.com/mail/?view=cm&fs=1` +
      `&to=${encodeURIComponent(to)}` +
      `&su=${encodeURIComponent(subject)}` +
      `&body=${encodeURIComponent(body)}`;

    window.open(gmailUrl, "_blank");
  };

  return (
    <div className="tasks-page-container">
      {!user ? (
        <p className="text-center text-gray-500">Please log in to view your tasks.</p>
      ) : (
        <>
          {successMessage && <p className="success-message">{successMessage}</p>}
          <div className="tasks-header">
            <h2>My Tasks</h2>
            <p className="muted-text">Overview of your assigned tasks</p>
          </div>

          {loading && <p className="loading-message">Loading tasks...</p>}
          {error && <p className="error-message">{error}</p>}

          {!loading && !error && userTasks.length === 0 && (
            <p className="muted-text" style={{ textAlign: 'center' }}>
              No tasks assigned to you yet.
            </p>
          )}

          {!loading && !error && (
            <div className="tasks-grid">
              {userTasks.map((userTask) => (
                <TaskCard
                  key={userTask.id}
                  task={userTask}
                  className={getTaskCardClassName(userTask.status)}
                  statusLabel={getTaskStatusLabel(userTask.status)}
                  onShowDetails={() => setSelectedTask(userTask)}
                />
              ))}
            </div>
          )}

          {selectedTask && (
            <div className="modal-overlay">
              <div className="modal-content">
                <h3>{selectedTask.title}</h3>
                <p>
                  <strong>Status:</strong> {getTaskStatusLabel(selectedTask.status)}
                </p>
                {selectedTask.rejection_message && (
                  <p className="error-message">
                    <strong>Admin Feedback:</strong> {selectedTask.rejection_message}
                  </p>
                )}
                <p style={{ whiteSpace: 'pre-wrap' }}>
                  <strong>Description:</strong> {selectedTask.description}
                </p>
                {selectedTask.tasks_url && (
                  <div>
                    <strong>URL:</strong>{' '}
                    {(() => {
                      const thumbnail = getYouTubeThumbnail(selectedTask.tasks_url);
                      return thumbnail ? (
                        <a href={selectedTask.tasks_url} target="_blank" rel="noopener noreferrer">
                          <img
                            src={thumbnail.primary}
                            alt="YouTube preview"
                            onError={(e) => {
                              // If maxresdefault fails, use hqdefault
                              if (e.target.src !== thumbnail.fallback) {
                                e.target.src = thumbnail.fallback;
                              }
                            }}
                            style={{
                              width: "100%",
                              maxWidth: "320px",
                              borderRadius: "8px",
                              marginTop: "8px",
                              display: "block"
                            }}
                          />
                        </a>
                      ) : (
                        <a href={selectedTask.tasks_url} target="_blank" rel="noopener noreferrer">
                          {selectedTask.tasks_url}
                        </a>
                      );
                    })()}
                  </div>
                )}
                <p>
                  <strong>Points:</strong> {selectedTask.points}
                </p>

                {(selectedTask.status === 'not_started' || selectedTask.status === 'in_progress') && (
                  <button type="button" onClick={handleStatusChange} className="change-status-button">
                    Change Status to "{selectedTask.status === 'not_started' ? 'In Progress' : 'Submit for Approval'}"
                  </button>
                )}

                {selectedTask.status === 'rejected' && (
                  <button type="button" onClick={handleRetryTask} className="change-status-button">
                    Retry Task
                  </button>
                )}

                <button type="button" onClick={closeTaskDetailsModal} className="modal-close-button">
                  Close
                </button>
              </div>
            </div>
          )}

          {showProofModal && (
            <div className="modal-overlay">
              <div className="modal-content proof-modal">
                <h3>Proof of Work</h3>
                <p>Have you sent the proof of work to the mail?</p>
                <div className="modal-actions">
                  <button type="button" onClick={handleSubmitForApproval} className="yes-button">
                    Yes, I have
                  </button>
                  <button onClick={handleSendProofEmail} className="no-button">
                    Take me to the mail
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default TasksPage;


